// src/components/auth/AuthContext.jsx
// Phase 1 auth rewrite (2026-04-23): client-side USERS dict deleted.
// All auth now talks to the backend /auth/* endpoints over HTTP-only cookies.
// See docs/phase-1-auth-design.md §8 for design rationale.

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();
const API_BASE = process.env.REACT_APP_API_URL || '';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Small fetch helper that always sends cookies. State-changing calls (POST/PUT/etc.)
// mirror the csrf_token cookie into the X-CSRF-Token header (double-submit pattern).
const _csrfHeader = () => {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? { 'X-CSRF-Token': decodeURIComponent(m[1]) } : {};
};

async function authFetch(path, { method = 'GET', body } = {}) {
  const needsCsrf = method !== 'GET' && method !== 'HEAD';
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(needsCsrf ? _csrfHeader() : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res;
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: ask the server who we are. If the access_token cookie is valid,
  // /auth/me returns the user profile. Any failure → we're logged out.
  const refreshUser = useCallback(async () => {
    try {
      const res = await authFetch('/auth/me');
      if (res.ok) {
        const profile = await res.json();
        setUser(profile);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error('Session check failed:', err);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshUser();
      setLoading(false);
    })();
  }, [refreshUser]);

  const login = async (username, password) => {
    try {
      const res = await authFetch('/auth/login', {
        method: 'POST',
        body: { username, password },
      });
      if (res.ok) {
        const profile = await res.json();
        setUser(profile);
        setIsAuthenticated(true);
        return { success: true, user: profile };
      }
      let detail = 'Invalid credentials';
      try {
        const body = await res.json();
        detail = body.detail || detail;
      } catch { /* non-JSON response */ }
      return { success: false, error: detail };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: 'Network error — please try again' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const res = await authFetch('/auth/change-password', {
        method: 'POST',
        body: { current_password: currentPassword, new_password: newPassword },
      });
      if (res.status === 204) {
        return { success: true };
      }
      let detail = 'Password change failed';
      try {
        const body = await res.json();
        detail = body.detail || detail;
      } catch { /* non-JSON response */ }
      return { success: false, error: detail };
    } catch (err) {
      console.error('Change password error:', err);
      return { success: false, error: 'Network error — please try again' };
    }
  };

  const logout = async () => {
    try {
      await authFetch('/auth/logout', { method: 'POST' });
    } catch (err) {
      // Even if the call fails, clear local state — the cookies may already be bad
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Back-compat for any code still calling hasPermission(). Post-rewrite,
  // callers should prefer explicit role checks (user.role === 'admin').
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return false;
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    changePassword,
    hasPermission,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
